import { useEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, ReactFlowProvider, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FolderSelect } from "@/components/FolderSelect";
import { computeLedger, fmtMoney, CURRENCY_SYMBOL, ACCOUNT_TYPES, accountType } from "@/lib/ledger";
import { useTheme } from "@/context/ThemeContext";
import { PAGE, BTN_PRIMARY, ICON_BTN, INPUT } from "@/lib/styles";
import { ArrowLeft, Save, Plus, Trash2, X, Landmark, Info, Scale, Undo2, Redo2, History, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const LedgerCtx = createContext(null);
const uid = () => "a_" + Math.random().toString(36).slice(2, 10);
const eid = () => "t_" + Math.random().toString(36).slice(2, 10);
const todayStr = () => new Date().toISOString().slice(0, 10);

function AccountNode({ id, data }) {
  const ctx = useContext(LedgerCtx);
  const t = accountType(data.type);
  const bal = ctx.balances[id] || 0;
  const dc = ctx.displayCurrency;
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 shadow-md w-56" style={{ borderColor: t.color }} data-testid={`ledger-account-${id}`}>
      <Handle id="out" type="source" position={Position.Right} className="!w-3 !h-3 !border-2 !border-white dark:!border-slate-900" style={{ background: t.color }} />
      <Handle id="in" type="target" position={Position.Left} className="!w-3 !h-3 !border-2 !border-white dark:!border-slate-900" style={{ background: t.color }} />
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color }} />
        <input
          value={data.name}
          onFocus={() => ctx.resetEdit()}
          onChange={(e) => { ctx.beginEdit(); ctx.updateAccount(id, { name: e.target.value }); }}
          className="nodrag flex-1 min-w-0 bg-transparent font-heading font-medium text-sm focus:outline-none"
          placeholder="Nombre (ej. Banco)"
          data-testid={`ledger-account-name-${id}`}
        />
        <button onClick={(e) => { e.stopPropagation(); ctx.removeAccount(id); }} className="text-slate-400 hover:text-rose-500 nodrag shrink-0" data-testid={`ledger-account-del-${id}`}><Trash2 size={13} /></button>
      </div>
      <div className="px-3 py-2 space-y-2">
        <select value={data.type} onChange={(e) => { ctx.snapshot(); ctx.updateAccount(id, { type: e.target.value }); }} className="nodrag w-full text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1" data-testid={`ledger-account-type-${id}`}>
          {ACCOUNT_TYPES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Saldo actual</p>
          <p className={`font-mono2 text-lg font-bold ${bal >= 0 ? "text-slate-800 dark:text-slate-100" : "text-rose-600"}`} data-testid={`ledger-account-balance-${id}`}>{fmtMoney(bal, dc)}</p>
        </div>
        <label className="nodrag flex items-center justify-between gap-2 text-[11px] text-slate-400">
          <span>Saldo inicial</span>
          <input type="number" value={data.opening ?? ""} onFocus={() => ctx.resetEdit()} onChange={(e) => { ctx.beginEdit(); ctx.updateAccount(id, { opening: Number(e.target.value) }); }} className="w-20 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-right font-mono2 text-slate-700 dark:text-slate-200" data-testid={`ledger-account-opening-${id}`} />
        </label>
      </div>
    </div>
  );
}

const nodeTypes = { account: AccountNode };

function TransferModal({ initial, preset, accounts, onSave, onDelete, onCancel }) {
  const [fromId, setFromId] = useState(initial?.from || preset?.from || "");
  const [toId, setToId] = useState(initial?.to || preset?.to || "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [currency, setCurrency] = useState(initial?.currency || "USD");
  const [concept, setConcept] = useState(initial?.concept || "");
  const [date, setDate] = useState(initial?.date ? initial.date.slice(0, 10) : todayStr());

  const nameOf = (aid) => accounts.find((a) => a.id === aid)?.name || "(cuenta)";

  const submit = () => {
    if (!fromId || !toId) return toast.error("Elige la cuenta de origen y destino.");
    if (fromId === toId) return toast.error("El origen y el destino no pueden ser la misma cuenta.");
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Escribe un monto válido.");
    onSave({ id: initial?.id || eid(), from: fromId, to: toId, amount: amt, currency, concept: concept.trim(), date });
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="ledger-transfer-modal">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg">{initial ? "Editar movimiento" : "Nuevo movimiento"}</h3>
          <button onClick={onCancel} className={ICON_BTN} data-testid="ledger-transfer-close"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="font-mono2 text-[11px] text-slate-500">Desde (resta)</label>
            <select className={`${INPUT} mt-1`} value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="ledger-transfer-from">
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || "(sin nombre)"}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-500">Hacia (suma)</label>
            <select className={`${INPUT} mt-1`} value={toId} onChange={(e) => setToId(e.target.value)} data-testid="ledger-transfer-to">
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || "(sin nombre)"}</option>)}
            </select>
          </div>
        </div>
        {fromId && toId && (
          <p className="text-[11px] text-slate-400 mb-3">Resta de <b>{nameOf(fromId)}</b> y suma a <b>{nameOf(toId)}</b>.</p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="font-mono2 text-[11px] text-slate-500">Monto</label>
            <input type="number" className={`${INPUT} mt-1`} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="ledger-transfer-amount" autoFocus />
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-500">Moneda</label>
            <select className={`${INPUT} mt-1`} value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="ledger-transfer-currency">
              <option value="USD">$ (Dólar)</option>
              <option value="VES">Bs (Bolívar)</option>
            </select>
          </div>
        </div>

        <label className="font-mono2 text-[11px] text-slate-500">Concepto (opcional)</label>
        <input className={`${INPUT} mt-1 mb-3`} value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="Ej: Pago de nómina" data-testid="ledger-transfer-concept" />

        <label className="font-mono2 text-[11px] text-slate-500">Fecha</label>
        <input type="date" className={`${INPUT} mt-1 mb-5`} value={date} onChange={(e) => setDate(e.target.value)} data-testid="ledger-transfer-date" />

        <div className="flex gap-2">
          <button onClick={submit} className={`${BTN_PRIMARY} flex-1`} data-testid="ledger-transfer-save">{initial ? "Guardar cambios" : "Agregar"}</button>
          {initial && <button onClick={() => onDelete(initial.id)} className={`${ICON_BTN} text-rose-500`} data-testid="ledger-transfer-delete"><Trash2 size={16} /></button>}
        </div>
      </div>
    </div>
  );
}

function Editor({ id }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [rate, setRate] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // {from,to} nuevo o {entry} editar
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("notacora_ledger_help") !== "0");
  const [showHistory, setShowHistory] = useState(false);
  const [histFilter, setHistFilter] = useState("");

  // --- Historial Deshacer/Rehacer ---
  const accRef = useRef([]);
  const entRef = useRef([]);
  useEffect(() => { accRef.current = accounts; }, [accounts]);
  useEffect(() => { entRef.current = entries; }, [entries]);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const editSession = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => { setCanUndo(pastRef.current.length > 0); setCanRedo(futureRef.current.length > 0); }, []);
  const snapshot = useCallback(() => {
    pastRef.current.push({ accounts: accRef.current, entries: entRef.current });
    if (pastRef.current.length > 60) pastRef.current.shift();
    futureRef.current = [];
    syncHist();
  }, [syncHist]);
  const beginEdit = useCallback(() => { if (!editSession.current) { snapshot(); editSession.current = true; } }, [snapshot]);
  const resetEdit = useCallback(() => { editSession.current = false; }, []);
  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    futureRef.current.push({ accounts: accRef.current, entries: entRef.current });
    const prev = pastRef.current.pop();
    setAccounts(prev.accounts); setEntries(prev.entries); syncHist();
  }, [syncHist]);
  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push({ accounts: accRef.current, entries: entRef.current });
    const nx = futureRef.current.pop();
    setAccounts(nx.accounts); setEntries(nx.entries); syncHist();
  }, [syncHist]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    api.get(`/notes/${id}`).then(({ data }) => {
      setTitle(data.title);
      setFolderId(data.folder_id || "");
      const l = data.ledger || {};
      setDisplayCurrency(l.displayCurrency || "USD");
      if (l.rate) setRate(l.rate);
      setAccounts(l.accounts || []);
      setEntries(l.entries || []);
    }).catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); navigate("/"); });
  }, [id, navigate]);

  useEffect(() => {
    api.get("/rates").then(({ data }) => { const u = (data.assets || []).find((a) => a.code === "usd_bcv"); if (u?.rate) setRate((p) => p || u.rate); }).catch(() => {});
  }, []);

  const { balances, byType, patrimonio, resultado } = useMemo(
    () => computeLedger(accounts, entries, { displayCurrency, rate }),
    [accounts, entries, displayCurrency, rate]
  );

  const updateAccount = useCallback((aid, patch) => setAccounts((as) => as.map((a) => (a.id === aid ? { ...a, ...patch } : a))), []);
  const removeAccount = useCallback((aid) => {
    snapshot();
    setAccounts((as) => as.filter((a) => a.id !== aid));
    setEntries((es) => es.filter((e) => e.from !== aid && e.to !== aid));
  }, [snapshot]);

  const addAccount = () => {
    snapshot();
    setAccounts((as) => {
      const n = as.length;
      return [...as, { id: uid(), name: `Cuenta ${n + 1}`, type: "activo", opening: 0, currency: "USD", x: 80 + (n % 3) * 280, y: 60 + Math.floor(n / 3) * 200 }];
    });
  };

  const nodes = useMemo(() => accounts.map((a) => ({ id: a.id, type: "account", position: { x: a.x || 0, y: a.y || 0 }, data: a })), [accounts]);

  const edges = useMemo(() => entries.map((e) => {
    const color = accountType(accounts.find((a) => a.id === e.from)?.type).color;
    return {
      id: e.id, source: e.from, target: e.to, sourceHandle: "out", targetHandle: "in",
      type: "smoothstep",
      label: fmtMoney(e.amount, e.currency),
      labelBgStyle: { fill: theme === "dark" ? "#0f172a" : "#fff" },
      labelStyle: { fontSize: 11, fill: theme === "dark" ? "#e2e8f0" : "#334155", fontWeight: 600 },
      style: { stroke: color, strokeWidth: 2 },
      markerEnd: { type: "arrowclosed", color },
    };
  }), [entries, accounts, theme]);

  const onConnect = useCallback((params) => {
    if (params.source === params.target) return;
    setModal({ from: params.source, to: params.target });
  }, []);
  const onEdgeClick = useCallback((evt, edge) => {
    evt.stopPropagation();
    const entry = entries.find((e) => e.id === edge.id);
    if (entry) setModal({ entry });
  }, [entries]);

  const saveEntry = (entry) => {
    snapshot();
    setEntries((es) => (es.some((e) => e.id === entry.id) ? es.map((e) => (e.id === entry.id ? entry : e)) : [...es, entry]));
    setModal(null);
  };
  const deleteEntry = (entryId) => { snapshot(); setEntries((es) => es.filter((e) => e.id !== entryId)); setModal(null); };

  const onNodeDragStop = useCallback((_e, node) => updateAccount(node.id, { x: node.position.x, y: node.position.y }), [updateAccount]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/notes/${id}`, { title: title || "Mapa Contable", ledger: { displayCurrency, rate, accounts, entries }, folder_id: folderId || null });
      toast.success("Guardado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const dismissHelp = () => { setShowHelp(false); localStorage.setItem("notacora_ledger_help", "0"); };
  const bgColor = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const ctxVal = { balances, displayCurrency, updateAccount, removeAccount, snapshot, beginEdit, resetEdit };

  const nameOf = (aid) => accounts.find((a) => a.id === aid)?.name || "(cuenta)";
  const fmtDate = (d) => { if (!d) return "—"; const [y, m, day] = String(d).split("-").map(Number); const dt = new Date(y, (m || 1) - 1, day || 1); return dt.toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" }); };
  const journal = useMemo(() => {
    const arr = entries.filter((e) => !histFilter || e.from === histFilter || e.to === histFilter);
    return arr.slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [entries, histFilter]);

  return (
    <div className={`${PAGE} font-body flex flex-col h-screen`}>
      <Navbar />
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex-wrap">
        <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="ledger-back"><ArrowLeft size={16} /></button>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="font-heading font-medium text-lg bg-transparent border-none focus:outline-none focus:ring-0 min-w-[140px] flex-1" placeholder="Mapa Contable" data-testid="ledger-title" />
        <FolderSelect value={folderId} onChange={setFolderId} allowNew />
        <button onClick={undo} disabled={!canUndo} className={`${ICON_BTN} disabled:opacity-40`} title="Deshacer (Ctrl+Z)" data-testid="ledger-undo"><Undo2 size={16} /></button>
        <button onClick={redo} disabled={!canRedo} className={`${ICON_BTN} disabled:opacity-40`} title="Rehacer (Ctrl+Y)" data-testid="ledger-redo"><Redo2 size={16} /></button>
        <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5">
          {["USD", "VES"].map((c) => (
            <button key={c} onClick={() => setDisplayCurrency(c)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${displayCurrency === c ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid={`ledger-currency-${c}`}>{CURRENCY_SYMBOL[c]}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono2 text-[11px] text-slate-500">Tasa</span>
          <input type="number" value={rate || ""} onChange={(e) => setRate(Number(e.target.value))} className="w-24 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-2 py-1 text-sm" placeholder="Bs/$" data-testid="ledger-rate" />
        </div>
        <button onClick={addAccount} className={BTN_PRIMARY} data-testid="ledger-add-account"><Plus size={16} /> Cuenta</button>
        <button onClick={() => accounts.length >= 2 ? setModal({ from: "", to: "" }) : toast.info("Crea al menos 2 cuentas primero.")} className={ICON_BTN} title="Nuevo movimiento entre cuentas" data-testid="ledger-add-transfer"><Scale size={16} /></button>
        <button onClick={() => setShowHistory((v) => !v)} className={`${ICON_BTN} ${showHistory ? "text-indigo-600 dark:text-indigo-400" : ""}`} title="Libro Diario (histórico de movimientos)" data-testid="ledger-history-toggle"><History size={16} /></button>
        <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="ledger-save"><Save size={16} /> {saving ? "..." : "Guardar"}</button>
      </div>

      {showHelp && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 border-b border-indigo-100 dark:border-indigo-500/20 text-xs text-indigo-800 dark:text-indigo-200" data-testid="ledger-help">
          <Info size={15} className="shrink-0 mt-0.5" />
          <p className="flex-1"><b>¿Cómo funciona?</b> Crea cuentas (Banco, Casa, Deudas…) y define su tipo y saldo inicial. Para registrar un movimiento, <b>arrastra un cable</b> desde el punto derecho de la cuenta de <b>origen</b> hasta la de <b>destino</b>: el monto <b>resta</b> del origen y <b>suma</b> al destino (siempre cuadra). Toca un cable para editarlo o borrarlo.</p>
          <button onClick={dismissHelp} className="shrink-0 text-indigo-500 hover:text-indigo-700" data-testid="ledger-help-close"><X size={14} /></button>
        </div>
      )}

      <div className="flex-1 relative">
        <LedgerCtx.Provider value={ctxVal}>
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} onConnect={onConnect} onEdgeClick={onEdgeClick} onNodeDragStart={() => snapshot()} onNodeDragStop={onNodeDragStop} fitView proOptions={{ hideAttribution: true }} minZoom={0.2}>
            <Background gap={22} size={1.5} color={bgColor} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </LedgerCtx.Provider>

        {accounts.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center pointer-events-auto max-w-sm">
              <Landmark size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="font-heading text-lg mb-1">Mapa Contable vacío</p>
              <p className="font-body text-sm text-slate-500 mb-4">Crea cuentas (Banco, Efectivo, Casa, Deudas…) y conéctalas con cables para mover dinero entre ellas. Los saldos se calculan solos.</p>
              <button onClick={addAccount} className={BTN_PRIMARY} data-testid="ledger-empty-add"><Plus size={16} /> Crear primera cuenta</button>
            </div>
          </div>
        ) : (
          <div className="absolute top-3 right-3 w-60 rounded-2xl border-2 border-emerald-500 dark:border-emerald-500/70 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-xl" data-testid="ledger-balance-panel">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-emerald-50 dark:bg-emerald-500/10 rounded-t-2xl">
              <Scale size={16} className="text-emerald-600 dark:text-emerald-400" />
              <span className="font-heading font-semibold text-sm">Balance General</span>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-xs">
              {ACCOUNT_TYPES.map((t) => (
                <div key={t.id} className="flex justify-between">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: t.color }} />{t.label}</span>
                  <span className="font-mono2" data-testid={`ledger-bytype-${t.id}`}>{fmtMoney(byType[t.id], displayCurrency)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-slate-800 text-sm">
                <span className="font-medium">Patrimonio (A−P)</span>
                <span className={`font-mono2 font-bold ${patrimonio >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600"}`} data-testid="ledger-patrimonio">{fmtMoney(patrimonio, displayCurrency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Resultado (I−G)</span>
                <span className={`font-mono2 font-bold ${resultado >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600"}`} data-testid="ledger-resultado">{fmtMoney(resultado, displayCurrency)}</span>
              </div>
            </div>
          </div>
        )}

        {showHistory && (
        <div className="absolute top-0 right-0 h-full w-full sm:w-96 bg-white/98 dark:bg-slate-900/98 backdrop-blur border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col z-20" data-testid="ledger-history-panel">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <History size={16} className="text-indigo-500 shrink-0" />
            <span className="font-heading font-semibold text-sm flex-1">Libro Diario <span className="text-slate-400 font-normal">· {journal.length}</span></span>
            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600" data-testid="ledger-history-close"><X size={16} /></button>
          </div>
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800">
            <select value={histFilter} onChange={(e) => setHistFilter(e.target.value)} className={INPUT} data-testid="ledger-history-filter">
              <option value="">Todas las cuentas</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || "(sin nombre)"}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {journal.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-10 px-4">
                {entries.length === 0 ? "Aún no hay movimientos. Conecta dos cuentas con un cable para registrar el primero." : "Ninguna cuenta seleccionada tiene movimientos."}
              </div>
            ) : journal.map((e) => {
              const fromOut = !histFilter || e.from === histFilter;
              return (
                <button key={e.id} onClick={() => setModal({ entry: e })} className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 px-3 py-2.5 transition" data-testid={`ledger-history-item-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono2 text-[10px] uppercase tracking-wide text-slate-400">{fmtDate(e.date)}</span>
                    <span className={`font-mono2 text-sm font-semibold ${histFilter ? (fromOut ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400") : "text-slate-800 dark:text-slate-100"}`}>
                      {histFilter ? (fromOut ? "−" : "+") : ""}{fmtMoney(e.amount, e.currency)}
                    </span>
                  </div>
                  {e.concept && <p className="text-sm font-medium mb-1 truncate">{e.concept}</p>}
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="truncate max-w-[42%]">{nameOf(e.from)}</span>
                    <ArrowRight size={12} className="shrink-0 text-slate-400" />
                    <span className="truncate max-w-[42%]">{nameOf(e.to)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>

      {modal && (
        <TransferModal
          initial={modal.entry}
          preset={modal.entry ? null : { from: modal.from, to: modal.to }}
          accounts={accounts}
          onSave={saveEntry}
          onDelete={deleteEntry}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}

export default function LedgerMap() {
  const { id } = useParams();
  return <ReactFlowProvider><Editor id={id} /></ReactFlowProvider>;
}
